import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, UserCheck } from 'lucide-react';
import { useStoreData } from '../hooks/useStoreData';
import { useCart } from '../contexts/CartContext';
import Header from '../components/Header';
import Cart from '../components/Cart';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { settings } = useStoreData();
  const { getCartCount } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Privacy Policy - Your Data Security | Rizzahra"
        description="Learn how Rizzahra protects your privacy and personal information. Our commitment to data security and transparency."
        keywords="privacy policy, data protection, security, customer information"
        url="/privacy-policy"
      />
      {/* Main Header */}
      <Header
        cartCount={getCartCount()}
        onCartClick={() => setIsCartOpen(true)}
        onAdminClick={() => navigate('/admin')}
      />

      {/* Page Header */}
      <header className="bg-[#4a1a1f] text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold uppercase tracking-wider">Privacy Policy</h1>
          <p className="text-gray-300 mt-2">Your privacy matters to us</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Last Updated: February 2025</strong>
            </p>
            <p className="text-gray-700 leading-relaxed">
              At {settings?.storeName || 'Rizzahra'}, we are committed to protecting your privacy and
              ensuring the security of your personal information. This Privacy Policy explains how we
              collect, use, store, and protect your data when you visit our website or make a purchase
              from our store.
            </p>
          </div>

          {/* Information We Collect */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <UserCheck className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">Information We Collect</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-lg mb-2">Personal Information</h3>
                <p className="mb-2">When you place an order or register on our site, we may collect:</p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Shipping address</li>
                  <li>Billing information</li>
                  <li>Payment details (processed securely through payment gateways)</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Automatically Collected Information</h3>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Pages visited on our website</li>
                  <li>Time and date of visits</li>
                  <li>Referring website addresses</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">Order Information</h3>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Purchase history</li>
                  <li>Products ordered</li>
                  <li>Order dates and amounts</li>
                  <li>Delivery preferences</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How We Use Information */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <Eye className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">How We Use Your Information</h2>
            </div>
            <div className="space-y-3 text-gray-700">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Process Orders:</strong> Fulfill your orders, arrange delivery, and provide customer support</li>
                <li><strong>Communication:</strong> Send order confirmations, shipping updates, and respond to inquiries</li>
                <li><strong>Improve Services:</strong> Analyze website usage to enhance user experience and product offerings</li>
                <li><strong>Marketing:</strong> Send promotional offers, new arrivals, and special deals (you can opt-out anytime)</li>
                <li><strong>Security:</strong> Prevent fraud and ensure secure transactions</li>
                <li><strong>Legal Compliance:</strong> Comply with applicable laws and regulations</li>
              </ul>
            </div>
          </div>

          {/* Data Security */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <Lock className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">Data Security</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Secure Socket Layer (SSL) encryption for data transmission</li>
                <li>Secure payment gateways for processing transactions</li>
                <li>Regular security audits and updates</li>
                <li>Restricted access to personal data (only authorized personnel)</li>
                <li>Secure servers and databases</li>
              </ul>
              <p className="font-semibold">
                Please note: No method of transmission over the internet is 100% secure. While we strive
                to protect your information, we cannot guarantee absolute security.
              </p>
            </div>
          </div>

          {/* Information Sharing */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#4a1a1f] rounded-full flex items-center justify-center">
                <Shield className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#4a1a1f]">Information Sharing</h2>
            </div>
            <div className="space-y-4 text-gray-700">
              <p>
                We <strong>do not sell, trade, or rent</strong> your personal information to third parties.
                We may share your information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>
                  <strong>Service Providers:</strong> Trusted third parties who assist us (shipping
                  companies, payment processors) under strict confidentiality agreements
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law or to protect our rights and
                  property
                </li>
                <li>
                  <strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets
                  (you will be notified)
                </li>
              </ul>
            </div>
          </div>

          {/* Cookies */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Cookies and Tracking Technologies</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                We use cookies and similar technologies to enhance your browsing experience:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for website functionality (shopping cart, checkout)</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                <li><strong>Marketing Cookies:</strong> Enable personalized advertising and promotions</li>
              </ul>
              <p>
                You can control cookie preferences through your browser settings. Note that disabling cookies
                may affect website functionality.
              </p>
            </div>
          </div>

          {/* Your Rights */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Your Rights</h2>
            <div className="space-y-3 text-gray-700">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal requirements)</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications anytime</li>
                <li><strong>Data Portability:</strong> Request your data in a structured format</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, contact us at {settings?.contactInfo?.email || 'our support email'}.
              </p>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Children's Privacy</h2>
            <p className="text-gray-700">
              Our website is not intended for children under 13 years of age. We do not knowingly collect
              personal information from children. If you believe we have collected information from a child,
              please contact us immediately.
            </p>
          </div>

          {/* Third-Party Links */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Third-Party Links</h2>
            <p className="text-gray-700">
              Our website may contain links to third-party websites. We are not responsible for the privacy
              practices of these sites. We encourage you to review their privacy policies before providing
              any personal information.
            </p>
          </div>

          {/* Changes to Policy */}
          <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
            <h2 className="text-2xl font-bold text-[#4a1a1f] mb-4">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy periodically to reflect changes in our practices or legal
              requirements. The "Last Updated" date at the top indicates when the policy was last revised.
              Continued use of our website after changes constitutes acceptance of the updated policy.
            </p>
          </div>

          {/* Contact */}
          <div className="bg-[#4a1a1f] text-white rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold mb-3">Questions About Privacy?</h3>
            <p className="text-gray-300 mb-6">
              If you have questions or concerns about our Privacy Policy or how we handle your data,
              please don't hesitate to contact us.
            </p>
            <div className="space-y-2 text-gray-300">
              {settings?.contactInfo?.email && (
                <p>Email: {settings.contactInfo.email}</p>
              )}
              {settings?.contactInfo?.phone && (
                <p>Phone: {settings.contactInfo.phone}</p>
              )}
              {settings?.contactInfo?.address && (
                <p>Address: {settings.contactInfo.address}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
};

export default PrivacyPolicy;
